import { type FieldError, type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';
import Input, { type InputProps as BaseInputProps } from '@repo/input';

export interface ControlledInputProps<TFieldValues extends FieldValues = FieldValues> extends Omit<BaseInputProps, 'error' | 'onChange' | 'onBlur' | 'name' | 'ref' | 'value' | 'defaultValue'> {
    name: FieldPath<TFieldValues>;
    control: Control<TFieldValues>;
    error?: FieldError;
    className?: string;
}

export default function ControlledInput<TFieldValues extends FieldValues = FieldValues>({
    name,
    control,
    error: errorProp,
    className = '',
    ...props
}: ControlledInputProps<TFieldValues>) {
    const { field, fieldState } = useController({
        control,
        name,
    });

    const error = fieldState.error || errorProp;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (props.type === 'number') {
            const numValue = parseFloat(event.target.value);
            field.onChange(event.target.value === '' ? undefined : (isNaN(numValue) ? undefined : numValue));
        } else {
            field.onChange(event.target.value);
        }
    };

    const handleBlur = () => {
        field.onBlur();
    };

    const displayValue = props.type === 'number'
        ? (field.value === undefined || field.value === null ? '' : String(field.value))
        : (field.value ?? '');

    return (
        <Input
            className={className}
            error={error?.message}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            {...props}
        />
    );
}
